import { ANALYST_VERIFICATIONS_CONFIG } from '@/constants/analyst-config';
import {
  VERIFICATION_RESULT,
  RISK_LEVEL,
  RUN_STATUS,
} from '@/constants/analyst-constants';
import { Statuses } from '@/V2/components/status/utils';
import * as trustScoreThresholds from '@/constants/trustScoreThresholds';
import { SEVERITY_LEVEL } from '@/constants/signalNames';
/**
 * get the verification title
 */
export function getVerificationTitle(verification) {
  const config = ANALYST_VERIFICATIONS_CONFIG[verification.type];
  return config?.label || verification.type;
}

/**
 * returns group Verification Count
 */
export function getGroupVerificationsCountByRisk(
  group,
  risk = VERIFICATION_RESULT.HIGH_RISK,
) {
  const highRisk = group?.riskLevels?.find(
    (riskLevel) => riskLevel.key === risk,
  );
  return highRisk?.verifications?.length || 0;
}

/**
 * returns all verifications count
 * @param {} group
 * @returns
 */
export function getGroupTotalCount(group) {
  let count = 0;
  group?.riskLevels?.forEach((riskLevel) => {
    count += riskLevel.verifications.length;
  });
  return count;
}

/**
 * return the last run from a collection
 * @param {*} runs
 * @returns
 */
export function getLastRun(runs) {
  let lastRun;
  if (runs?.length) {
    const copyRuns = [...runs];
    copyRuns.sort((run1, run2) =>
      new Date(run1.created_at) < new Date(run2.created_at) ? 1 : -1,
    );
    lastRun = copyRuns[0];
  }
  return lastRun;
}

/**
 * map agent run objects to a label string.
 * This can be used to be passed as input to stutuses components such as StatusBadge, StatusBar, StatusCard etc.
 * @param {*} run
 * @returns
 */
export function mapRunToLabel(run) {
  if (!run) return 'Analyzing';
  if (!run.output && run.status === RUN_STATUS.FAILED) {
    return 'Analysis Failed';
  } else if (run.status === RUN_STATUS.FINISHED) {
    return mapRiskLevelToLabel(run.output?.risk_level);
  } else if ([RUN_STATUS.PENDING, RUN_STATUS.RUNNING].includes(run.status)) {
    return 'Analyzing';
  } else if ([RUN_STATUS.CANCELLED, RUN_STATUS.FAILED].includes(run.status)) {
    return 'Not Applicable';
  }
}

/**
 * map risk level to label. This is used for the fraud risk badge, status bar etc.
 * It returns the label displayed for each risk level and it should be the only place where the mapping is done.
 * @param {*} riskLevel
 * @returns
 */
export function mapRiskLevelToLabel(riskLevel) {
  if (riskLevel === RISK_LEVEL.HIGH) {
    return 'High Risk';
  } else if (riskLevel === RISK_LEVEL.MEDIUM) {
    return 'Medium Risk';
  } else if (riskLevel === RISK_LEVEL.LOW) {
    return 'Low Risk';
  } else if (riskLevel === RISK_LEVEL.NOT_APPLICABLE) {
    return 'Not Applicable';
  }
}

/**
 * map agent run objects to a status value from Statuses object.
 * This can be used to be passed as input to stutuses components such as StatusBadge, StatusBar, StatusCard etc.
 * @param {*} run
 * @returns
 */
export function mapRunToStatus(run) {
  if (!run) return Statuses.LOADING;
  if (!run.output && run.status === RUN_STATUS.FAILED) {
    return Statuses.FAILED;
  } else if (run.status === RUN_STATUS.FINISHED) {
    return mapRiskLevelToStatus(run.output?.risk_level);
  } else if ([RUN_STATUS.PENDING, RUN_STATUS.RUNNING].includes(run.status)) {
    return Statuses.LOADING;
  } else if ([RUN_STATUS.CANCELLED, RUN_STATUS.FAILED].includes(run.status)) {
    return Statuses.NOT_APPLICABLE;
  }
}

/**
 * map risk level to a status value from Statuses object.
 * This can be used to be passed as input to stutuses components such as StatusBadge, StatusBar, StatusCard etc.
 * @param {*} riskLevel
 * @returns
 */
export function mapRiskLevelToStatus(riskLevel) {
  if (riskLevel === RISK_LEVEL.HIGH) {
    return Statuses.ERROR;
  } else if (riskLevel === RISK_LEVEL.MEDIUM) {
    return Statuses.WARNING;
  } else if (riskLevel === RISK_LEVEL.LOW) {
    return Statuses.SUCCESS;
  } else if (riskLevel === RISK_LEVEL.NOT_APPLICABLE) {
    return Statuses.NOT_APPLICABLE;
  }
}

export function mapDocumentToRiskLevel(document) {
  if (document.trust_score === undefined || document.trust_score === null) {
    return 'Not Applicable';
  } else if (document.trust_score <= trustScoreThresholds.LOW) {
    return 'High Risk';
  } else if (document.trust_score >= trustScoreThresholds.HIGH) {
    return 'Low Risk';
  } else {
    return 'Medium Risk';
  }
}

export function mapDocumentToStatus(document) {
  if (document.trust_score === undefined || document.trust_score === null) {
    return Statuses.NOT_APPLICABLE;
  } else if (document.trust_score <= trustScoreThresholds.LOW) {
    return Statuses.ERROR;
  } else if (document.trust_score >= trustScoreThresholds.HIGH) {
    return Statuses.SUCCESS;
  } else {
    return Statuses.WARNING;
  }
}

export function mapSeverityToStatus(severity) {
  if (severity === SEVERITY_LEVEL.HIGH) {
    return Statuses.ERROR;
  } else if (severity === SEVERITY_LEVEL.MEDIUM) {
    return Statuses.WARNING;
  } else if (severity === SEVERITY_LEVEL.LOW) {
    return Statuses.SUCCESS;
  } else {
    return Statuses.NOT_APPLICABLE;
  }
}
